import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getModerator, getModeratorSites, assignModeratorSites, getSites, updateModerator, resetModeratorPassword, resetModeratorClientPassword, addModeratorClient } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Shield, ArrowLeft, Mail, Phone, Building, Calendar, MapPin, 
  Zap, Users, BarChart3, Key, Plus, MoreHorizontal, Copy, Check,
  TrendingUp, Battery, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

const ModeratorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [moderator, setModerator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allSites, setAllSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  
  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [clientPasswordDialogOpen, setClientPasswordDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Form states
  const [editFormData, setEditFormData] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [newClientData, setNewClientData] = useState({ email: '', full_name: '', phone: '', password: '' });
  const [clientNewPassword, setClientNewPassword] = useState('');
  
  // Date filter for stats
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  
  const fetchModerator = useCallback(async () => {
    try {
      const [modRes, sitesRes] = await Promise.all([
        getModerator(id),
        getSites()
      ]);
      setModerator(modRes.data);
      setAllSites(sitesRes.data);
      setSelectedSites(modRes.data.sites?.map(s => s.id) || []);
      setEditFormData({
        full_name: modRes.data.full_name || '',
        phone: modRes.data.phone || '',
        address: modRes.data.address || '',
        city: modRes.data.city || '',
        nipt: modRes.data.nipt || ''
      });
    } catch (error) {
      console.error('Failed to fetch moderator:', error);
      toast.error('Gabim në ngarkimin e moderatorit');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchModerator();
  }, [fetchModerator]);

  const handleUpdateModerator = async (e) => {
    e.preventDefault();
    try {
      await updateModerator(id, editFormData);
      toast.success('Moderatori u përditësua');
      setEditDialogOpen(false);
      fetchModerator();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në përditësim');
    }
  };

  const handleAssignSites = async () => {
    try {
      await assignModeratorSites(id, selectedSites);
      toast.success('Stacionet u caktuan');
      setAssignDialogOpen(false);
      fetchModerator();
    } catch (error) {
      toast.error('Gabim në caktimin e stacioneve');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }
    try {
      await resetModeratorPassword(id, newPassword);
      toast.success('Fjalëkalimi u ndryshua');
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në ndryshimin e fjalëkalimit');
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await addModeratorClient(id, newClientData);
      toast.success('Klienti u shtua');
      setAddClientDialogOpen(false);
      setNewClientData({ email: '', full_name: '', phone: '', password: '' });
      fetchModerator();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim në shtimin e klientit');
    }
  };

  const handleResetClientPassword = async () => {
    if (clientNewPassword.length < 6) {
      toast.error('Fjalëkalimi duhet të ketë të paktën 6 karaktere');
      return;
    }
    try {
      await resetModeratorClientPassword(id, selectedClient.id, clientNewPassword);
      toast.success('Fjalëkalimi i klientit u ndryshua');
      setClientPasswordDialogOpen(false);
      setClientNewPassword('');
      setSelectedClient(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim');
    }
  };

  const toggleSiteSelection = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-10 w-64" />
        <div className="skeleton h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (!moderator) {
    return (
      <div className="text-center py-12">
        <Shield className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <p className="text-zinc-400">Moderatori nuk u gjet</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/moderators')}>
          Kthehu
        </Button>
      </div>
    );
  }

  const stats = moderator.stats || {};

  return (
    <div className="space-y-6" data-testid="moderator-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/moderators')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Shield className="w-7 h-7 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-outfit">{moderator.full_name}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-zinc-400">{moderator.email}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setResetPasswordDialogOpen(true)}>
            <Key className="w-4 h-4 mr-2" />
            Ndrysho Fjalëkalimin
          </Button>
          <Button onClick={() => setEditDialogOpen(true)}>
            Modifiko Profilin
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_clients || 0}</p>
                <p className="text-sm text-zinc-400">Klientë</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Battery className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_energy_kwh?.toFixed(1) || 0}</p>
                <p className="text-sm text-zinc-400">kWh Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_revenue_lek?.toLocaleString() || 0} Kredite</p>
                <p className="text-sm text-zinc-400">Të Ardhura</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_sessions || 0}</p>
                <p className="text-sm text-zinc-400">Sesione</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-800/50 border border-white/10">
          <TabsTrigger value="info" className="data-[state=active]:bg-zinc-700">
            Info të Përgjithshme
          </TabsTrigger>
          <TabsTrigger value="sites" className="data-[state=active]:bg-zinc-700">
            Stacionet ({moderator.sites?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="chargers" className="data-[state=active]:bg-zinc-700">
            Karikuesit ({moderator.chargers?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="clients" className="data-[state=active]:bg-zinc-700">
            Klientët ({moderator.clients?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="stats" className="data-[state=active]:bg-zinc-700">
            Statistika
          </TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Informacioni i Kontaktit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                  <Mail className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500">Email</p>
                    <p className="text-sm text-white">{moderator.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                  <Phone className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500">Telefoni</p>
                    <p className="text-sm text-white">{moderator.phone || 'Pa specifikuar'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                  <Building className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500">Adresa</p>
                    <p className="text-sm text-white">
                      {moderator.address || 'Pa specifikuar'}
                      {moderator.city && `, ${moderator.city}`}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                  <Building className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500">NIPT</p>
                    <p className="text-sm text-white">{moderator.nipt || 'Pa specifikuar'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50">
                  <Calendar className="w-5 h-5 text-zinc-500" />
                  <div>
                    <p className="text-xs text-zinc-500">Data e Regjistrimit</p>
                    <p className="text-sm text-white">{formatDate(moderator.created_at)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sites Tab */}
        <TabsContent value="sites" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Stacionet e Caktuara</CardTitle>
                <CardDescription>Stacionet që menaxhon ky moderator</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setAssignDialogOpen(true)}>
                <MapPin className="w-4 h-4 mr-2" />
                Cakto Stacione
              </Button>
            </CardHeader>
            <CardContent>
              {(!moderator.sites || moderator.sites.length === 0) ? (
                <div className="text-center py-8 text-zinc-500">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka stacione të caktuara</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Emri</TableHead>
                      <TableHead className="text-zinc-400">Adresa</TableHead>
                      <TableHead className="text-zinc-400">Qyteti</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {moderator.sites.map(site => (
                      <TableRow key={site.id} className="border-white/5">
                        <TableCell className="text-white font-medium">{site.name}</TableCell>
                        <TableCell className="text-zinc-300">{site.address}</TableCell>
                        <TableCell className="text-zinc-300">{site.city}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Chargers Tab */}
        <TabsContent value="chargers" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Karikuesit</CardTitle>
              <CardDescription>Karikuesit në stacionet e caktuara</CardDescription>
            </CardHeader>
            <CardContent>
              {(!moderator.chargers || moderator.chargers.length === 0) ? (
                <div className="text-center py-8 text-zinc-500">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka karikues</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">ID</TableHead>
                      <TableHead className="text-zinc-400">Model</TableHead>
                      <TableHead className="text-zinc-400">Fuqia</TableHead>
                      <TableHead className="text-zinc-400">Statusi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {moderator.chargers.map(charger => (
                      <TableRow key={charger.id} className="border-white/5">
                        <TableCell className="text-white font-mono">{charger.charger_id}</TableCell>
                        <TableCell className="text-zinc-300">{charger.model}</TableCell>
                        <TableCell className="text-zinc-300">{charger.max_power_kw} kW</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            charger.is_online 
                              ? 'bg-emerald-500/10 text-emerald-400' 
                              : 'bg-red-500/10 text-red-400'
                          }`}>
                            {charger.is_online ? 'Online' : 'Offline'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white">Klientët e Lidhur</CardTitle>
                <CardDescription>Klientët që paguajnë me cash te ky moderator</CardDescription>
              </div>
              <Button onClick={() => setAddClientDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Shto Klient
              </Button>
            </CardHeader>
            <CardContent>
              {(!moderator.clients || moderator.clients.length === 0) ? (
                <div className="text-center py-8 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <p>Nuk ka klientë të lidhur</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-400">Klienti</TableHead>
                      <TableHead className="text-zinc-400">Telefoni</TableHead>
                      <TableHead className="text-zinc-400">Sesione</TableHead>
                      <TableHead className="text-zinc-400">Energji</TableHead>
                      <TableHead className="text-zinc-400">Shpenzuar</TableHead>
                      <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {moderator.clients.map(client => (
                      <TableRow key={client.id} className="border-white/5">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{client.full_name}</p>
                            <p className="text-xs text-zinc-500">{client.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-300">{client.phone || '-'}</TableCell>
                        <TableCell className="text-zinc-300">{client.total_sessions || 0}</TableCell>
                        <TableCell className="text-zinc-300">{client.total_energy_kwh?.toFixed(1) || 0} kWh</TableCell>
                        <TableCell className="text-zinc-300">{client.total_spent_lek?.toLocaleString() || 0} Kredite</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedClient(client);
                                  setClientPasswordDialogOpen(true);
                                }}
                                className="text-zinc-300 focus:bg-white/10"
                              >
                                <Key className="w-4 h-4 mr-2" />
                                Ndrysho Fjalëkalimin
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="mt-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Statistikat e Detajuara</CardTitle>
              <CardDescription>Statistikat e aktivitetit të moderatorit</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-lg bg-zinc-800/50 text-center">
                  <p className="text-3xl font-bold text-white">{stats.total_sessions || 0}</p>
                  <p className="text-sm text-zinc-400 mt-1">Sesione Totale</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-800/50 text-center">
                  <p className="text-3xl font-bold text-emerald-400">{stats.total_energy_kwh?.toFixed(1) || 0}</p>
                  <p className="text-sm text-zinc-400 mt-1">kWh Totale</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-800/50 text-center">
                  <p className="text-3xl font-bold text-amber-400">{stats.total_revenue_lek?.toLocaleString() || 0}</p>
                  <p className="text-sm text-zinc-400 mt-1">Kredite Të Ardhura</p>
                </div>
                <div className="p-4 rounded-lg bg-zinc-800/50 text-center">
                  <p className="text-3xl font-bold text-blue-400">{stats.total_clients || 0}</p>
                  <p className="text-sm text-zinc-400 mt-1">Klientë</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-zinc-800/50">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Karikues Online</span>
                    <span className="text-emerald-400 font-bold">{stats.online_chargers || 0}</span>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-zinc-800/50">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Karikues Offline</span>
                    <span className="text-red-400 font-bold">{stats.offline_chargers || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Modifiko Moderatorin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateModerator} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emri i Plotë</Label>
                <Input
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Telefoni</Label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adresa</Label>
                <Input
                  value={editFormData.address}
                  onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Qyteti</Label>
                <Input
                  value={editFormData.city}
                  onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>NIPT</Label>
              <Input
                value={editFormData.nipt}
                onChange={(e) => setEditFormData({ ...editFormData, nipt: e.target.value })}
                placeholder="Numri i Identifikimit Tatimor"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Sites Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Cakto Stacione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {allSites.map(site => (
              <div 
                key={site.id} 
                className="flex items-center space-x-3 p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 cursor-pointer"
                onClick={() => toggleSiteSelection(site.id)}
              >
                <Checkbox
                  checked={selectedSites.includes(site.id)}
                  onCheckedChange={() => toggleSiteSelection(site.id)}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{site.name}</p>
                  <p className="text-xs text-zinc-500">{site.address}, {site.city}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleAssignSites}>Ruaj ({selectedSites.length} stacione)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Ndrysho Fjalëkalimin</DialogTitle>
            <DialogDescription>Vendosni fjalëkalimin e ri për moderatorin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fjalëkalimi i Ri</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 karaktere"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleResetPassword}>Ndrysho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={addClientDialogOpen} onOpenChange={setAddClientDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Shto Klient të Ri</DialogTitle>
            <DialogDescription>Klienti do të paguajë vetëm me cash</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddClient} className="space-y-4">
            <div className="space-y-2">
              <Label>Emri i Plotë *</Label>
              <Input
                value={newClientData.full_name}
                onChange={(e) => setNewClientData({ ...newClientData, full_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newClientData.email}
                onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Telefoni</Label>
              <Input
                value={newClientData.phone}
                onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Fjalëkalimi</Label>
              <Input
                type="password"
                value={newClientData.password}
                onChange={(e) => setNewClientData({ ...newClientData, password: e.target.value })}
                placeholder="Lini bosh për albmove123"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddClientDialogOpen(false)}>Anulo</Button>
              <Button type="submit">Shto Klientin</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Password Dialog */}
      <Dialog open={clientPasswordDialogOpen} onOpenChange={setClientPasswordDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Ndrysho Fjalëkalimin e Klientit</DialogTitle>
            <DialogDescription>{selectedClient?.full_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fjalëkalimi i Ri</Label>
              <Input
                type="password"
                value={clientNewPassword}
                onChange={(e) => setClientNewPassword(e.target.value)}
                placeholder="Minimum 6 karaktere"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientPasswordDialogOpen(false)}>Anulo</Button>
            <Button onClick={handleResetClientPassword}>Ndrysho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModeratorDetail;
